import {Component, inject} from '@angular/core';
import {BaseModalComponent} from '../base-modal/base-modal.component';
import {DocumentModalService} from './services/document-modal.service';
import {
  DocumentData,
  DocumentDialogData,
  Options
} from './types/document-modal.types';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatAnchor, MatButton} from '@angular/material/button';
import {CommonModule} from "@angular/common";
import {
  allowedEditRoles,
  documentResourceType,
  documentRows, documentsResourceType
} from "./configs/documents-model-config";
import {DateInputDirective} from "../../../../shared/directives/date-input.directive";
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerInputEvent,
  MatDatepickerToggle
} from "@angular/material/datepicker";
import {MatFormField, MatLabel, MatSuffix} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatOption} from "@angular/material/core";
import {MatSelect} from "@angular/material/select";
import {IconStatus} from "../../../models/icon-status-config";
import {OpenDocumentModalParams} from "./types/document-files.types";
import {IconStatusDirective} from "../../../../shared/directives/icon-status.directive";
import {DocumentOpenModalService} from "./services/document-open-modal.service";
import {DocumentStatusService} from "./services/document-status.service";
import {DocumentDateService} from "./services/document-date.service";
import {isDocumentDateKey} from "./configs/document-date-config";
import {
  DocumentUpdateData,
  DocumentUpdateKey,
  DocumentUpdateParams
} from "./types/document-update.types";
import {defaultDocumentUpdateData} from "./models/document-update-date";
import {
  isDocumentUpdateDataKey,
  isDocumentUpdateStatusKey,
  safeDocumentCopy
} from "./configs/document-update-data-config";
import {FilesStatus} from "../../../types/files-status.types";
import {defaultDocumentsFilesStatus} from "./configs/documents-modal-row-config";
import {customDateProvider} from "../../../locale/providers/custom-date.provider";

@Component({
  selector: 'app-documents-modal',
  standalone: true,
  imports: [
    BaseModalComponent,
    ReactiveFormsModule,
    MatButton,
    FormsModule,
    CommonModule,
    MatAnchor,
    DateInputDirective,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
    IconStatusDirective
  ],
  templateUrl: './documents-modal.component.html',
  styleUrls: [
    '../base-modal/base-modal.component.scss',
    './documents-modal.component.scss'
  ],
  providers: [ ...customDateProvider ]
})
export class DocumentsModalComponent extends BaseModalComponent {
  private dialogData = inject<DocumentDialogData>(MAT_DIALOG_DATA);
  private documentModalService: DocumentModalService = inject(DocumentModalService);
  private documentOpenModalService: DocumentOpenModalService = inject(DocumentOpenModalService);
  private documentStatusService: DocumentStatusService = inject(DocumentStatusService);
  private documentDateService: DocumentDateService = inject(DocumentDateService);

  public readonly documentResourceType = documentResourceType;
  public readonly documentRows = documentRows;
  public documentForm: FormGroup = new FormGroup({});
  public documentsData: DocumentData | null = null;
  public hasEditPermission: boolean = false;
  public status: FilesStatus = defaultDocumentsFilesStatus;
  private recordData!: { documents: { id: number } };
  private documentUpdateData: DocumentUpdateData = defaultDocumentUpdateData;

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    this.modalStatesService.openModal(documentsResourceType.path);
    this.documentForm = this.documentModalService.createForm();
    await this.loadDocumentData();
    this.hasEditPermission = this.userHasAllowedEditRole();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(documentsResourceType.path);
  }

  public async handleUpdateDocument() {
    this.documentModalService.setStatusFields(this.documentForm);
    const documentUpdateParams: DocumentUpdateParams = {
      documentId: this.recordData['documents']!.id,
      updateData: this.documentUpdateData
    };

    await this.documentModalService.updateDocumentData(documentUpdateParams)
      .then((document) => {
        Object.assign(this.documentsData!, document);
        Object.assign(this.documentForm, document);
        Object.assign(this.documentUpdateData, safeDocumentCopy(document));
        this.snackbarService.showMessage({ message: 'Данные успешно обновлены!' })
      })
      .catch(() => this.closeDialog('cancel'));
  }

  public handleDateChange(date: MatDatepickerInputEvent<any, any>, key: string): void {
    const formattedDate = date.value ? this.utilityService.formatDateToRussianStyle(date.value) : null;
    if (isDocumentDateKey(key) && this.documentsData?.[key]) {
      const currentDate = this.documentsData[key]?.date;
      const validMessage = this.documentDateService.validDateMessage(this.documentForm, key);
      if (validMessage) {
        this.errorService.showErrorMessage(validMessage);
        this.documentForm.get(key)?.get('date')?.setValue(currentDate);
      } else {
        this.documentUpdateData[key].date = formattedDate;
        this.documentsData[key].date = date.value;
      }
    }
  }

  public handleSelectChange(value: string, key: string): void {
    if (isDocumentUpdateStatusKey(key) && this.documentsData?.[key]) {
      this.documentUpdateData[key].status = value;
    }
  }

  public handleInputChange(key: string, fieldName: string): void {
    const newValue = this.documentForm.get(key)?.get(fieldName as string)?.value;
    if (isDocumentUpdateDataKey(key)) {
      (this.documentUpdateData[key as DocumentUpdateKey] as Record<string, any>)[fieldName] = newValue;
    }
  }

  public getBtnIconStatus(key: string): IconStatus {
    return this.documentStatusService.getBtnIconStatus({
      key: key,
      documentsData: this.documentsData
    });
  }

  public isDisabledButton(): boolean {
    return !this.isEditModeActive || this.status?.generatingStatus === 'ERROR';
  }

  public getCurrentStatusValue(key: string): string | null {
    return this.documentStatusService.getCurrentStatusValue({
      key: key,
      documentsData: this.documentsData
    });
  }

  public getStatusOptions(key: string): Array<Options> {
    return this.documentStatusService.getStatusOptions({
      key: key,
      documentsData: this.documentsData
    });
  }

  public async handleBtnClick(key: string): Promise<void> {
    if (!this.documentsData) { return; }

    const openDocumentModalParams: OpenDocumentModalParams = {
      recordData: this.dialogData.recordData,
      documentsData: this.documentsData,
      key: key,
      isEditMode: this.isEditModeActive
    };

    await this.documentOpenModalService.openFilesDocumentModal(openDocumentModalParams);
    this.status = await this.documentStatusService.getFilesStatus(Number(this.recordData['documents']?.id));
  }

  public getTextClasses(key: string): string {
     return key === 'bankDetails' ? 'none-pointer-events' : this.setFieldClasses();
  }

  public fetchIconStatus(statusName: string | undefined): IconStatus {
    return statusName ? this.getIconStatus(this.status[statusName] as IconStatus) : 'ERROR';
  }

  private userHasAllowedEditRole(): boolean {
    const userRoles = this.authService.getUserRoles();
    return userRoles.some(role => allowedEditRoles.includes(role));
  }

  private async loadDocumentData(): Promise<void> {
    this.recordData = this.dialogData.recordData as unknown as { documents: { id: number } }
    this.status = await this.documentStatusService.getFilesStatus(Number(this.recordData['documents']?.id));

    await this.documentModalService.getDocumentsData(this.recordData['documents'].id)
      .then((documentData) => {
        this.documentsData = this.documentDateService.convertDates(documentData);
        this.documentUpdateData = safeDocumentCopy(documentData);
        this.documentForm.patchValue(this.documentsData);
      })
      .catch(() => this.closeDialog('cancel'));
  }
}
